<script lang="ts">
  import { resolve } from '$app/paths'
  import { navigating, page } from '$app/state'
  import { clickOutside } from '$lib/actions/click-outside'

  let stateInput: HTMLInputElement | undefined = $state()

  const changeMenuVisibility = (visible: boolean) => {
    if (stateInput) stateInput.checked = visible
  }
  $effect(() => {
    if (navigating.to) {
      changeMenuVisibility(false)
    }
  })
</script>

<!--
  Wrapping in a separate div, because when the label is pressed,
  there is also a click event on the input
-->
<div use:clickOutside onoutclick={() => changeMenuVisibility(false)}>
  <input
    aria-hidden="true"
    bind:this={stateInput}
    type="checkbox"
    id="menustate"
  />

  <nav>
    <div class="hamburger-container">
      <a
        class="open"
        role="button"
        href="#menustate"
        onclick={(e) => {
          e.preventDefault()
          changeMenuVisibility(true)
        }}
        onkeypress={(e) => {
          e.preventDefault()
          changeMenuVisibility(true)
        }}
      >
        <span class="screen-reader-text">Open menu</span>
      </a>

      <!-- svelte-ignore a11y_invalid_attribute -->
      <a
        class="close"
        role="button"
        href="#"
        onclick={(e) => {
          e.preventDefault()
          changeMenuVisibility(false)
        }}
        onkeypress={(e) => {
          e.preventDefault()
          changeMenuVisibility(false)
        }}
      >
        <span class="screen-reader-text">Close menu</span>
      </a>

      <label for="menustate" aria-hidden="true" class="unstyled">
        <svg viewBox="0 0 24 24" width="1.2em" height="1.2em">
          <g stroke-width="2" stroke="currentColor">
            <g class="burger-crust-top">
              <line x1="0" y1="4" x2="23" y2="4" />
            </g>
            <g class="burger-patty">
              <line x1="0" y1="11" x2="23" y2="11" />
            </g>
            <g class="burger-crust-bottom">
              <line x1="0" y1="19" x2="23" y2="19" />
            </g>
          </g>
        </svg>
      </label>
    </div>

    <ul>
      <li class:active={page.url.pathname === '/'} class="home">
        <a href={resolve('/')} aria-label="Home">Home</a>
      </li>
      <li class:active={page.url.pathname === '/business'}>
        <a href={resolve('/business')}>Business</a>
      </li>
      <li class:active={page.url.pathname === '/pricing'}>
        <a href={resolve('/pricing')}>Pricing</a>
      </li>
      <li class:active={page.url.pathname.startsWith('/help')}>
        <a href={resolve('/help')}>Help</a>
      </li>
    </ul>
  </nav>
</div>

<style lang="postcss">
  @media (min-width: 720px) {
    .hamburger-container {
      display: none;
    }
  }
  #menustate {
    display: none;
  }

  .hamburger-container {
    position: relative;
    width: 2.75rem;
    height: 2.75rem;

    & a,
    & label {
      position: absolute;
      inset: 0;
    }
    & label {
      display: grid;
      place-content: center;
    }

    & g {
      transition: all 300ms cubic-bezier(0.04, 0.04, 0.12, 0.96) 120ms;
    }
    & line {
      transition: all 180ms cubic-bezier(0.04, 0.04, 0.12, 0.96);
    }

    & g,
    & line {
      transform-box: fill-box;
      transform-origin: center;
    }
  }

  nav {
    position: relative;
    z-index: var(--layer-top);
  }

  ul {
    padding: 0;
    list-style: none;
    white-space: nowrap;
    & li {
      height: 3.5rem;
      & a {
        display: inline-flex;
        align-items: center;
        padding-inline: 1rem;
        height: 100%;
        color: var(--color-text);
        font-weight: var(--weight-semibold);
        &:hover {
          text-decoration: underline;
          text-decoration-color: var(--color-border);
          text-decoration-thickness: 3px;
          text-underline-offset: 0.3em;
        }
      }
      &.active {
        & a {
          text-decoration: underline;
          text-decoration-color: var(--color-bg-accent);
          text-decoration-thickness: 3px;
          text-underline-offset: 0.3em;
        }
      }
    }
  }

  @media (min-width: 720px) {
    /* Desktop view */
    nav ul {
      display: flex;
      align-items: center;
      height: 3.5rem;

      & li:first-child a {
        padding-left: 0;
      }
    }
  }
  @media (max-width: 719px) {
    /* Mobile view */
    nav ul {
      display: flex;
      position: absolute;
      top: calc(0px - 0.5rem);
      left: calc(0px - 0.25rem);
      flex-direction: column;
      translate: 0 -10px;
      scale: 1 0.9;
      visibility: hidden;
      opacity: 0;
      transition: all 150ms ease-in-out;
      box-shadow: 5px 6px var(--color-bg-accent-secondary);
      border: 2px solid var(--color-border);
      border-radius: var(--radius-xl);
      background: var(--color-bg-surface);
      padding: 0 0.75rem;
      padding-inline: 0;
      pointer-events: none;
      & li {
        translate: 0 -10px;
        opacity: 0;
        transition: all 150ms ease-in-out;
        &:not(:first-child)::after {
          position: absolute;
          top: -1px;
          right: 0;
          left: 0;
          background: var(--color-bg-surface);
          height: 1px;
          content: '';
        }

        & a {
          padding-inline: 3rem;
          width: 100%;
        }
      }
    }

    a {
      z-index: var(--layer-3);
    }
    label {
      z-index: var(--layer-4);
      cursor: pointer;
    }
    a.open {
      display: block;
    }
    a.close {
      display: none;
    }
    #menustate:target,
    #menustate:checked {
      & ~ nav {
        /* Open menu */
        & ul {
          display: flex;
          translate: 0;
          scale: 1;
          visibility: visible;
          opacity: 1;
          pointer-events: all;
          & li {
            --transition-delay: 80ms;
            translate: 0 0;
            opacity: 1;
            transition: all 350ms ease-out;

            &:nth-child(1) {
              transition-delay: calc(0 * var(--transition-delay));
            }
            &:nth-child(2) {
              transition-delay: calc(1 * var(--transition-delay));
            }
            &:nth-child(3) {
              transition-delay: calc(2 * var(--transition-delay));
            }
            &:nth-child(4) {
              transition-delay: calc(3 * var(--transition-delay));
            }
            &:nth-child(5) {
              transition-delay: calc(4 * var(--transition-delay));
            }
            &:nth-child(6) {
              transition-delay: calc(5 * var(--transition-delay));
            }
          }
        }
        & a.open {
          display: none;
        }
        & a.close {
          display: block;
        }

        & .burger-crust-top {
          transform: translateY(7px);
          & line {
            transform: rotate(45deg);
          }
        }
        & .burger-patty {
          opacity: 0;
        }
        & .burger-crust-bottom {
          transform: translateY(-8px);
          & line {
            transform: rotate(-45deg);
          }
        }

        & g {
          transition: all 180ms cubic-bezier(0.04, 0.04, 0.12, 0.96);
        }
        & line {
          transition: all 300ms cubic-bezier(0.04, 0.04, 0.12, 0.96) 120ms;
        }
      }
    }
  }
</style>
